import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import StatusIconCDS from 'app/shared/componentsRestyled/coreDesignSystem/StatusIconCDS';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AiFormFieldReviewState } from 'constants/aiProcessRuns';
import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { StatusEnum } from 'constants/status';

import { reviewFormFieldAction } from 'store/aiProcessRuns';
import { selectIsClaimAllowed } from 'store/auth/selectors';

import CitedText from '../../../../../AiActivityCitations/CitedText';
import FormFieldProvenance from '../../FormFieldProvenance';
import FormFieldValue from '../../FormFieldValue';
import {
  formatRawValue,
  isTableDiffUnverified,
  resolveFieldTriageTone,
  resolveOverrideInitialValue,
  serializeOverrideValue,
} from '../../formReview.utils';
import {
  FORM_REVIEW_LABELS,
  FormReviewTestIds,
  TRIAGE_TONE_TO_STATUS,
} from '../../FormReviewCard.constants';
import FormFieldOverrideInput from '../FormFieldOverrideInput';
import {
  changedBadgeStyles,
  fieldRowActionsStyles,
  fieldRowHeaderStyles,
  fieldRowLabelStyles,
  getFieldRowStyles,
  overrideEditorActionsStyles,
  overrideEditorStyles,
} from '../FormReviewSurface.styles';

const CONFIDENCE_PERCENT_FACTOR = 100;

const FormReviewFieldRow = ({
  field,
  schemaField,
  tableDiff,
  formId,
  isActionable,
  isInFlight,
  isFullWidth = false,
  onOpenDocument,
}) => {
  const dispatch = useDispatch();
  const [isOverriding, setIsOverriding] = useState(false);
  const [overrideInput, setOverrideInput] = useState('');

  const triageTone = useMemo(
    () => resolveFieldTriageTone(field, { tableDiff, fieldType: schemaField?.type }),
    [field, schemaField?.type, tableDiff],
  );
  const triageStatus = TRIAGE_TONE_TO_STATUS[triageTone];
  const canViewConfidence = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.AI_VIEW_CONFIDENCE_SCORES),
  );
  const confidence = _.isNumber(field?.confidence) ? field.confidence : null;
  const isUnverified = useMemo(() => isTableDiffUnverified(tableDiff), [tableDiff]);
  const rowStyles = useMemo(() => getFieldRowStyles({ isFullWidth }), [isFullWidth]);

  const changedAfterReview = useMemo(() => {
    if (field?.changedAfterReview !== true) {
      return null;
    }

    return {
      was: formatRawValue(field.previousReviewedValue, schemaField),
      now: formatRawValue(field.value, schemaField),
    };
  }, [field?.changedAfterReview, field?.previousReviewedValue, field?.value, schemaField]);

  // A schema field the AI never filled has no AiFormFieldValue to review — the
  // review endpoint keys on an existing field value, so showing Agree/Override/Flag
  // for it would only crash. Gate the actions on a present, keyed field value.
  const canReview = isActionable && !!field?.fieldKey;

  useEffect(() => {
    if (isOverriding) {
      setOverrideInput(resolveOverrideInitialValue(field, schemaField));
    }
  }, [field, isOverriding, schemaField]);

  const onAgree = useCallback(() => {
    if (!field?.fieldKey) {
      return;
    }

    dispatch(
      reviewFormFieldAction({
        formId,
        fieldKey: field.fieldKey,
        reviewState: AiFormFieldReviewState.AGREED,
      }),
    );
  }, [dispatch, field?.fieldKey, formId]);

  const onFlag = useCallback(() => {
    if (!field?.fieldKey) {
      return;
    }

    dispatch(
      reviewFormFieldAction({
        formId,
        fieldKey: field.fieldKey,
        reviewState: AiFormFieldReviewState.FLAGGED,
      }),
    );
  }, [dispatch, field?.fieldKey, formId]);

  const onOverrideOpen = useCallback(() => {
    setIsOverriding(true);
  }, []);

  const onOverrideCancel = useCallback(() => {
    setIsOverriding(false);
    setOverrideInput('');
  }, []);

  const onOverrideSave = useCallback(async () => {
    if (!field?.fieldKey) {
      return;
    }

    try {
      await dispatch(
        reviewFormFieldAction({
          formId,
          fieldKey: field.fieldKey,
          reviewState: AiFormFieldReviewState.OVERRIDDEN,
          overriddenValue: serializeOverrideValue(overrideInput, schemaField?.type),
        }),
      );
      setIsOverriding(false);
      setOverrideInput('');
    } catch {
      // editor stays open; error toast already shown by catchAsyncActionError
    }
  }, [dispatch, field?.fieldKey, formId, overrideInput, schemaField?.type]);

  return (
    <Box data-testid={FormReviewTestIds.SURFACE_FIELD_ROW} sx={rowStyles}>
      <Box sx={fieldRowHeaderStyles}>
        <Box sx={fieldRowLabelStyles}>
          {triageStatus ? (
            <Box data-testid={FormReviewTestIds.FIELD_TRIAGE_ICON}>
              <StatusIconCDS size={16} type={triageStatus} />
            </Box>
          ) : null}
          <Typography color="text.secondary" variant="bold12">
            {schemaField?.label ?? field?.fieldKey}
          </Typography>
        </Box>
        {confidence !== null && canViewConfidence ? (
          <Typography color="text.secondary" variant="regular12">
            {`${FORM_REVIEW_LABELS.CONFIDENCE}: ${Math.round(
              confidence * CONFIDENCE_PERCENT_FACTOR,
            )}%`}
          </Typography>
        ) : null}
      </Box>
      <FormFieldValue field={field} schemaField={schemaField} tableDiff={tableDiff} />
      {isUnverified ? (
        <Box data-testid={FormReviewTestIds.UNVERIFIED_BADGE} sx={changedBadgeStyles}>
          <StatusIconCDS size={16} type={StatusEnum.WARNING} />
          <Typography color="warning.dark" variant="regular12">
            {FORM_REVIEW_LABELS.UNVERIFIED_NOTE}
          </Typography>
        </Box>
      ) : null}
      {changedAfterReview ? (
        <Box data-testid={FormReviewTestIds.CHANGED_AFTER_REVIEW} sx={changedBadgeStyles}>
          <StatusIconCDS size={16} type={StatusEnum.WARNING} />
          <Typography color="warning.dark" variant="regular12">
            <CitedText
              text={`${FORM_REVIEW_LABELS.CHANGED_AFTER_REVIEW} — ${FORM_REVIEW_LABELS.CHANGED_WAS} ${changedAfterReview.was}, ${FORM_REVIEW_LABELS.CHANGED_NOW} ${changedAfterReview.now}`}
            />
          </Typography>
        </Box>
      ) : null}
      <FormFieldProvenance field={field} onOpenDocument={onOpenDocument} />
      {field?.respondedByUserName ? (
        <Typography color="text.secondary" variant="regular12">
          {`${field.reviewState} by ${field.respondedByUserName}`}
        </Typography>
      ) : null}
      {canReview && isOverriding ? (
        <Box sx={overrideEditorStyles}>
          <FormFieldOverrideInput
            disabled={isInFlight}
            schemaField={schemaField}
            value={overrideInput}
            onChange={setOverrideInput}
          />
          <Box sx={overrideEditorActionsStyles}>
            <Button
              data-testid={FormReviewTestIds.OVERRIDE_CANCEL}
              disabled={isInFlight}
              size="small"
              variant="line"
              onClick={onOverrideCancel}
            >
              {FORM_REVIEW_LABELS.OVERRIDE_CANCEL}
            </Button>
            <Button
              data-testid={FormReviewTestIds.OVERRIDE_SAVE}
              disabled={isInFlight}
              size="small"
              onClick={onOverrideSave}
            >
              {FORM_REVIEW_LABELS.OVERRIDE_SAVE}
            </Button>
            {isInFlight ? <CircularProgress size={14} thickness={4} /> : null}
          </Box>
        </Box>
      ) : null}
      {canReview && !isOverriding ? (
        <Box sx={fieldRowActionsStyles}>
          <Button
            data-testid={FormReviewTestIds.AGREE_BUTTON}
            disabled={isInFlight}
            size="small"
            variant={field?.reviewState === AiFormFieldReviewState.AGREED ? undefined : 'line'}
            onClick={onAgree}
          >
            {FORM_REVIEW_LABELS.AGREE}
          </Button>
          <Button
            data-testid={FormReviewTestIds.OVERRIDE_BUTTON}
            disabled={isInFlight}
            size="small"
            variant={field?.reviewState === AiFormFieldReviewState.OVERRIDDEN ? undefined : 'line'}
            onClick={onOverrideOpen}
          >
            {FORM_REVIEW_LABELS.OVERRIDE}
          </Button>
          <Button
            data-testid={FormReviewTestIds.FLAG_BUTTON}
            disabled={isInFlight}
            size="small"
            variant={field?.reviewState === AiFormFieldReviewState.FLAGGED ? undefined : 'line'}
            onClick={onFlag}
          >
            {FORM_REVIEW_LABELS.FLAG}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

FormReviewFieldRow.propTypes = {
  formId: PropTypes.string.isRequired,
  isActionable: PropTypes.bool,
  isInFlight: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  field: PropTypes.shape({
    fieldKey: PropTypes.string,
    confidence: PropTypes.number,
    reviewState: PropTypes.string,
    respondedByUserName: PropTypes.string,
    changedAfterReview: PropTypes.bool,
    previousReviewedValue: PropTypes.string,
    value: PropTypes.string,
  }),
  schemaField: PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    allowedValues: PropTypes.arrayOf(PropTypes.string),
  }),
  tableDiff: PropTypes.shape({
    fieldKey: PropTypes.string,
    rows: PropTypes.array,
  }),
  onOpenDocument: PropTypes.func,
};

export default memo(FormReviewFieldRow);
